// Libs
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTextEditor, Loading, UnderDevelopment } from '~/components/common';
// Others
import { ModeTextEditorEnum } from '~/utils/enum/common';
import { defaultValuesForm, schema } from './helper';
import { IFormNote, INote, IQueryUpdateNote } from '~/utils/interface/note';
import { useAppDispatch } from '~/utils/redux/hooks';
import { createNote, updateNote } from '~/thunks/notes/notesThunk';
import { notesActions } from '~/thunks/notes/notesSlice';
import { compareDataUpdate } from '~/utils/helper/common';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons

type Props = {
  note?: INote;
  onClose: () => void;
};

const FormNoteTimeClock = (props: Props) => {
  //#region Destructuring Props
  const { note, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/timeClock']);
  const dispatch = useAppDispatch();
  const { id: businessId } = useParams();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormNote>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormNote>(defaultValuesForm);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setInitialData({ content: note?.content || EMPTY_STRING });
    reset({ ...defaultValuesForm, content: note?.content || EMPTY_STRING });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseFormNote = () => {
    onClose();
    reset(defaultValuesForm);
  };

  const handleCreateNote = (data: IFormNote) => {
    if (!businessId) return;

    const payload: IFormNote = {
      ...data,
      businessId: businessId,
    };

    setIsLoading(true);

    dispatch(createNote(payload))
      .unwrap()
      .then((res) => {
        dispatch(notesActions.setIsRefreshNote(true));
        handleCloseFormNote();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleUpdateNote = (data: IFormNote) => {
    if (!businessId || !note?.id) return;

    const dataChange = compareDataUpdate(initialData, data) as IFormNote;

    const payload: IQueryUpdateNote = {
      noteId: note?.id,
      body: {
        content: dataChange?.content,
      },
    };

    setIsLoading(true);

    dispatch(updateNote(payload))
      .unwrap()
      .then((res) => {
        dispatch(notesActions.setIsRefreshNote(true));
        handleCloseFormNote();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmitFormNote = (data: IFormNote) => {
    if (note?.id) {
      handleUpdateNote(data);
    } else {
      handleCreateNote(data);
    }
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='formNoteTimeClockComponent'
      open={true}
      onClose={handleCloseFormNote}
      fullWidth
      maxWidth={'md'}
    >
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitFormNote)}>
        <DialogTitle>{t(note?.id ? 'form_note_time_clock_edit' : 'form_note_time_clock_add')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={12}>
              <Controller
                name='content'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseTextEditor
                    mode={ModeTextEditorEnum.CREATE}
                    height={200}
                    label={t('form_note_time_clock_field_note')}
                    value={value}
                    onChange={(textEditor: string) => onChange(textEditor)}
                    required
                    errorMessage={errors.content?.message}
                  />
                )}
              />
            </Grid2>
          </Grid2>

          {isLoading && <Loading />}
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleCloseFormNote}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={note?.id ? !isDirty : false}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </Dialog>
  );
};

export default FormNoteTimeClock;
